import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from 'primereact/dialog';
import { Sidebar } from 'primereact/sidebar';
import { Tooltip } from 'primereact/tooltip';

import { Button, InputSwitchField } from '../../helpers';
import Table from '../../helpers/Table';
import Menu from "../../helpers/Menu"

import SearchBar from './Components/SearchBar';
import AddPettyCashAccountModal from './Components/AddPettyCashAccountModal';
import PettyCashUsersModal from './Components/PettyCashUsersModal';

import Loading from '../../common/Loading';
import ButtonGroup from '../../common/ButtonGroup';

import { fetchPettyCashAccounts, updatePettyCashAccountsData, setIsSearchVisible } from "../../services/redux/reducers/pettyCashAccounts/pettyCashAccounts";
import { updatePettyCashAccountStatusById } from '../../services/api/pettyCashAccounts';
import { raiseToast } from '../../services/redux/reducers/toast/toast';

import "./index.css"
import { isMobile } from '../../utils';

import { getFullName } from '../../utils';
import { useSearchPin } from '../../hooks/useSearchPin';


const PettyCashAccounts = () => {
    const dispatch = useDispatch()
    const { isSearchPinned, toggleSearchPin, setSearchPin } = useSearchPin()
    
    const [visibleAddPettyCashAccount, setVisibleAddPettyCashAccount] = useState(false)
    const [visiblePettyCashUsers, setVisiblePettyCashUsers] = useState(false)
    const [selectedPettyCashUsers, setSeletedPettyCashUsers] = useState([])
    const [selectedRowData, setSelectedRowData] = useState(null)
    const [title, setTitle] = useState('')
    const [pettyCashAccountLoading, setPettyCashAccountLoading] = useState(false)
    const [isHoveringSearchZone, setIsHoveringSearchZone] = useState(false)
    const [hasEverHoveredRightEdge, setHasEverHoveredRightEdge] = useState(false)

    const paginationParams = useSelector(state => state.pettyCashAccounts.paginationParams)
    const searchParams = useSelector(state => state.pettyCashAccounts.searchParams)
    const pettyCashAccountsData = useSelector(state => state.pettyCashAccounts.pettyCashAccountsData)
    const isLoading = useSelector(state => state.pettyCashAccounts.loading)
    const isSearchVisible = useSelector(state => state.pettyCashAccounts.isSearchVisible)
    const isSearchActive = useSelector(state => state.pettyCashAccounts.isSearchActive)
    const userDetails = useSelector(state => state.userDetails)
    const usersList = userDetails.PMCompanyDetails.companyUsers

    const allPMBranches = userDetails.PMCompanyDetails.companyBranches

    const actionMenu = useRef(null)

    const isMobileDevice = isMobile()

    useEffect(() => {
        if (!isSearchPinned && !isHoveringSearchZone) {
            dispatch(setIsSearchVisible(false))
          }

        const handleMouseMove = (e) => {
            if (isSearchPinned) return

            const panelWidth = window.innerWidth * 0.18
            const buffer = 8
            const screenRightEdge = window.innerWidth
            const activeZoneLeft = screenRightEdge - (panelWidth + buffer)

            if (e.clientX < activeZoneLeft) {
                setIsHoveringSearchZone(false)
                setHasEverHoveredRightEdge(false)
            } else {
                setIsHoveringSearchZone(true)
            }
        }

        if (!isSearchPinned && (isHoveringSearchZone || hasEverHoveredRightEdge)) {
            window.addEventListener('mousemove', handleMouseMove)
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [isSearchPinned, isHoveringSearchZone, hasEverHoveredRightEdge])

    const handlePageChange = async (e) => {
        const pettyCashAccountsSearchParams = {
            ...searchParams,
            paginator: true,
            pageNo: e.page + 1,
            recordsPerPage: e.rows,
            limitedPagination: true,
            getCount: false,
        }
        if (pettyCashAccountsSearchParams.searchBy !== "all") {
            pettyCashAccountsSearchParams[pettyCashAccountsSearchParams.searchBy] = pettyCashAccountsSearchParams.searchText.trim()
        }
        dispatch(fetchPettyCashAccounts({ ...pettyCashAccountsSearchParams, include: [{ model: "pettyCashUsers", attributes: ["userId"] }] }));
    }

    const handlePettyCashUsersDisplay = (row) => {
        const selectedUsers = row.accountUsers.map(user => {
            const userObj = usersList.find(u => u.userId === user.userId)
            if (userObj) {
                return { userName: `${userObj.firstName} ${userObj.lastName}`, email: userObj.emailId }
            } else {
                return null;
            }
        }).filter(user => user !== null)
        setSeletedPettyCashUsers(selectedUsers)
        setVisiblePettyCashUsers(true)
    }

    const getPrimaryHolderBody = (row) => {
        const relatedUser = usersList.find(eachUser => eachUser.userId === row.userId)

        return relatedUser ? getFullName(relatedUser.firstName, relatedUser.lastName) : "-"
    }

    const getAccountUsersBody = (row) => {
        const userObj = usersList.find(eachUser => eachUser.userId === row.userId)
        let primaryUser = ''
        if (userObj) {
            primaryUser = `${userObj?.firstName} ${userObj?.lastName}`
        }
        return (
            <>
                <Button label='View' onClick={() => handlePettyCashUsersDisplay(row)} />
            </>
        )
    }

    const getPrimaryUserEmailBody = (row) => {
        const relatedPrimaryUser = usersList.find(userUser => userUser.userId === row.userId)
        return (
            relatedPrimaryUser ?
                <div>
                    {relatedPrimaryUser.emailId}
                    <i className="pi pi-copy pl-1 doc-copy-icon" style={{ cursor: "pointer", color: "#0da487" }} onClick={() => handleCopyClick(relatedPrimaryUser.emailId)}></i>
                </div>
                :
                "-"
        )
    }

    const handleEdit = (rowData) => {
        setSelectedRowData(rowData)
        setTitle('EDIT')
        setVisibleAddPettyCashAccount(true)
    }

    const actionMenuItems = [
        {
            label: 'Add PettyCash Account',
            icon: '',
            command: () => { setTitle('ADD'); setSelectedRowData(null); setVisibleAddPettyCashAccount(true) }
        },
    ]

    const handleUpdateAccountStatus = async (row) => {
        if (row.statusId === 1) {
            if (window.confirm("Do you want to deactivate this account?")) {
                setPettyCashAccountLoading(true)
                const updatedResponse = await updatePettyCashAccountStatusById(row.pettyCashAccountsId, { statusId: 2 })
                setPettyCashAccountLoading(false)

                if (updatedResponse.failure) {
                    return dispatch(raiseToast({ severity: 'error', detail: "Account can't found", life: 3000 }))
                } else {
                    const updatedList = pettyCashAccountsData.map((eachPettyCashAccount) => {
                        if (eachPettyCashAccount.pettyCashAccountsId === row?.pettyCashAccountsId) {
                            return {
                                ...eachPettyCashAccount,
                                statusId: 2,
                            }
                        } else {
                            return { ...eachPettyCashAccount }
                        }
                    })
                    dispatch(updatePettyCashAccountsData(updatedList))
                    return dispatch(raiseToast({ severity: 'success', detail: "Account deactivated successfully.", life: 3000 }))
                }
            }
        } else {
            if (window.confirm("Do you want to activate this account?")) {
                setPettyCashAccountLoading(true)
                const updatedResponse = await updatePettyCashAccountStatusById(row?.pettyCashAccountsId, { statusId: 1 })
                setPettyCashAccountLoading(false)

                if (updatedResponse.failure) {
                    return dispatch(raiseToast({ severity: 'error', detail: "Account can't found", life: 3000 }))
                } else {
                    const updatedList = pettyCashAccountsData.map((eachPettyCashAccount) => {
                        if (eachPettyCashAccount.pettyCashAccountsId === row?.pettyCashAccountsId) {
                            return {
                                ...eachPettyCashAccount,
                                statusId: 1,
                            }
                        } else {
                            return { ...eachPettyCashAccount }
                        }
                    })
                    dispatch(updatePettyCashAccountsData(updatedList))
                    if (updatedResponse?.data?.accountUsers.length === 0) {
                        return dispatch(raiseToast({ severity: 'success', summary: 'Account activated', detail: "Please add users to account.", life: 3000 }))
                    }
                    return dispatch(raiseToast({ severity: 'success', detail: "Account activated successfully.", life: 3000 }))
                }
            }
        }
    }

    const getStatusBody = (row) => {
        return (
            <InputSwitchField onChange={() => handleUpdateAccountStatus(row)} checked={row?.statusId === 1 ? true : false} />
        )
    }

    const getPmBranchBody = (row) => {
        if (!row.pmBranchId) {
            return (
                <div> - </div>
            )
        }
        const relatedPMBranch = allPMBranches.find(eachPMBranch => eachPMBranch.branchId === row.pmBranchId)
        return (
            <div> {relatedPMBranch.branchName} </div>
        )
    }

    const handleSearchTabDisplay = () => {
        if (!isSearchPinned) {
          dispatch(setIsSearchVisible(!isSearchVisible))
        }
    }

    const handleCopyClick = (text) => {
        if (text) {
            navigator.clipboard.writeText(text)
                .then(() => {
                    return dispatch(raiseToast({ severity: 'success', detail: "Copied!", life: 3000 }))
                })
                .catch((error) => {
                    return dispatch(raiseToast({ severity: 'error', detail: "Can't copy", life: 3000 }))
                })
        }
    }

    const getPettyCashAccountBody = (row) => {
        return (
            <div>
                {row.pettyCashAccount}
                <i className="pi pi-copy pl-1 doc-copy-icon" style={{ cursor: "pointer", color: "#0da487" }} onClick={() => handleCopyClick(row.pettyCashAccount)} ></i>
            </div>
        )
    }

    const tableColumns = [
        {
            header: 'Account Name',
            field: 'pettyCashAccount',
            body: getPettyCashAccountBody
        },
        {
            header: 'PM Branch',
            field: '',
            body: getPmBranchBody
        },
        {
            header: 'Primary Holder',
            field: 'userId',
            body: getPrimaryHolderBody
        },
        {
            header: 'User Email',
            field: 'userEmail',
            body: getPrimaryUserEmailBody
        },
        {
            header: 'Status',
            field: 'statusId',
            body: getStatusBody
        },
        {
            header: 'Account Users',
            field: 'userId',
            body: getAccountUsersBody
        },
        {
            header: 'Edit',
            body: (rowData) => (
                <Button icon="pi pi-pencil" className="p-button-rounded" onClick={() => handleEdit(rowData)} />
            )
        }
    ]

    const actionButtons = [
        {
            label: "Action",
            command: (event) => actionMenu.current?.toggle(event),
        },
        {
            icon: "pi pi-filter",
            label: "Filter",
            command: () => handleSearchTabDisplay(),
            className: isSearchActive ? "active-group-button" : ""
        },
        {
            icon: isSearchPinned ? "pi pi-lock" : "pi pi-lock-open",
            command: () => toggleSearchPin() 
        }
    ]

    let displaySearchBox = isSearchPinned || isHoveringSearchZone || isSearchVisible

    return (
        <div className='flex flex-column'>
            <Loading visible={pettyCashAccountLoading} />
            <div
                onMouseEnter={() => {
                    if (!isSearchPinned) {
                        setIsHoveringSearchZone(true)
                        setHasEverHoveredRightEdge(true)
                        dispatch(setIsSearchVisible(true))
                    }
                }}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '8px',
                    height: '100vh',
                    zIndex: 999
                }}
            />
            <div className="flex flex-column md:flex-row md:justify-content-between">
                <h4 className="mx-2 my-1 md:m-2">PETTY CASH ACCOUNTS</h4>
                <div className="flex gap-2 justify-content-end mb-2">
                    <Menu model={actionMenuItems} popup menuRef={actionMenu} />
                    {/* <Button className="h-2rem p-2 action-btn hover:surface-400 hover:text-700 surface-300" label="Action" onClick={(e) => actionMenu.current.toggle(e)} />
                    <Button icon="pi pi-filter" className={`h-2rem  p-2 action-btn hover:surface-400 hover:text-700 surface-300 ${isSearchActive ? 'search-filter-active' : ''}`} label="Filter" onClick={handleSearchTabDisplay} /> */}
                    <ButtonGroup buttons={actionButtons} />
                </div>
            </div>

            <div className='flex flex-row'>
                <div className="pettycash-accounts-table" style={{ order: "1", width: displaySearchBox && !isMobileDevice ? "82%" : "100%", maxHeight: "'calc(100%-100px)'" }}>
                    <div className='p-card mb-0' >
                        <div className="flex-grow-1" >
                            <Table
                                lazy
                                page={paginationParams.pageNo}
                                onPageChange={(e) => handlePageChange(e)}
                                totalRecords={paginationParams.totalRecords}
                                tableColumns={tableColumns}
                                data={pettyCashAccountsData}
                                rows={paginationParams.recordsPerPage}
                                scrollable={"true"}
                                scrollHeight="550px"
                                paginator
                                excessPagesFound={paginationParams.excessPagesFound}
                                loading={isLoading}
                            />
                        </div>
                    </div>
                </div>
                {displaySearchBox && (
                    <div
                        style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end"
                        onMouseLeave={(e) => {
                            const bounds = e.currentTarget.getBoundingClientRect()
                            const leftExit = e.clientX < bounds.left
                            const topExit = e.clientY < bounds.top
                            const bottomExit = e.clientY > bounds.bottom

                            if (!isSearchPinned && (leftExit || topExit || bottomExit)) {
                                setIsHoveringSearchZone(false)
                                setHasEverHoveredRightEdge(false)
                            }
                        }}

                        onMouseEnter={() => {
                            if (!isSearchPinned) {
                                setIsHoveringSearchZone(true)
                            }
                        }}
                    >
                        <SearchBar />
                    </div>
                )}
                {/* {
                    isSearchVisible && !isMobileDevice &&
                    <div style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end">
                        <SearchBar />
                    </div>
                } */}
                {
                    isSearchVisible && isMobileDevice &&
                    <div style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end">
                        <Sidebar position='right' visible={isSearchVisible} onHide={() => dispatch(setIsSearchVisible(false))}>
                            <SearchBar />
                        </Sidebar>
                    </div>
                }
            </div>
            <Dialog header={`${title} PETTY CASH ACCOUNT`} visible={visibleAddPettyCashAccount} style={{ width: '50vw', minHeight: "40vh", }} onHide={() => setVisibleAddPettyCashAccount(false)}>
                <AddPettyCashAccountModal initialValues={selectedRowData} usersList={usersList} title={title} setVisibleAddPettyCashAccount={setVisibleAddPettyCashAccount} setPettyCashAccountLoading={setPettyCashAccountLoading} />
            </Dialog>
            <Dialog header="ACCOUNT USERS" visible={visiblePettyCashUsers} style={{ width: '30vw', minHeight: "30vh", }} onHide={() => setVisiblePettyCashUsers(false)}>
                <PettyCashUsersModal selectedPettyCashUsers={selectedPettyCashUsers} />
            </Dialog>
        </div>
    )
}

export default PettyCashAccounts;